<template>
    <app-framework>
      <header class="container">
        <bread-crumbs :list="[
          { name: $t('home'), link: '/' },
          { name: $t('menu_listing'), link: '/menus' },
          { name: $t('product_listing') }
        ]" />
        <h1>{{ $t('product_listing') }}</h1>
      </header>
      <article class="container framework-slot">
        <div class="form-row toolbar">
            <div class="col col-auto">
                <router-link :to="`/menus/${$route.params.menuId}/product-categories/new`" class="btn btn-primary">{{ $t("new_category_button") }}</router-link>
            </div>
            <div class="col col-auto">
                <router-link :to="`/menus/${$route.params.menuId}/products/new`" class="btn btn-primary">{{ $t("new_product_button") }}</router-link>
            </div>
            <div class="col d-block"></div>
            <div class="col col-auto text-right">
                <button class="btn btn-outline-secondary" :title="$t('refresh_data')" :disabled="productCategories.loading"
                        v-on:click="refreshData">
                    <font-awesome-icon :spin="productCategories.loading" :icon="['fal', 'sync-alt']"/>
                </button>
            </div>
            <div class="col col-12 col-md-auto">
                <button v-if="!productCategories.reorderEnabled" class="btn btn-outline-secondary btn-block" :disabled="productCategories.loading" @click="productCategories.reorderEnabled = true">{{ $t("reorder_categories") }}</button>
                <button v-if="productCategories.reorderEnabled && !productCategories.savingReordering" class="btn btn-outline-primary btn-block" v-on:click="saveProductCategoriesReordering()">{{ $t("reorder_categories_ok") }}</button>
                <button v-if="productCategories.reorderEnabled && productCategories.savingReordering" :disabled="true" class="btn btn-outline-primary btn-block">{{ $t("reorder_categories_saving") }}</button>
            </div>
        </div>
        <div v-if="productCategories.loading">
            <loading-state/>
        </div>
        <div v-else-if="products.error || productCategories.error">
            <request-error-state :try-again-handler="refreshData" :message="products.error || productCategories.error"/>
        </div>
        <div v-else-if="!productCategories.data.nodes || !productCategories.data.nodes.length">
            <empty-state/>
        </div>
        <div v-else class="product-categories" :class="{'reorder-product-categories-enabled': productCategories.reorderEnabled}">
            <div class="alert alert-danger alert-dismissible" role="alert" v-if="productCategories.reorderError">
                {{productCategories.reorderError}}
                <button type="button" class="close" data-dismiss="alert" aria-label="Close" @click="productCategories.reorderError = null">
                    <span aria-hidden="true">&times;</span>
                </button>
            </div>
            <draggable v-model="productCategories.data.nodes" :group="'product-categories'" :disabled="!productCategories.reorderEnabled">
                <template v-for="(productCategory, productCategoryIndex) in productCategories.data.nodes">
                    <div :key="productCategory.id" class="product-category">
                        <div class="product-category-header form-row">
                            <div class="col-md col-12">
                                <h2>{{productCategory.name}}</h2>
                            </div>
                            <div v-if="productCategory.products.length > 1 && !productCategories.reorderEnabled" class="col-auto">
                                <button v-if="!productCategory.reorderEnabled" class="btn btn-outline-secondary btn-sm" v-on:click="productCategory.reorderEnabled = true">{{ $t("reorder_products") }}</button>
                                <button v-if="productCategory.reorderEnabled && !productCategory.savingReordering" class="btn btn-outline-primary btn-sm" v-on:click="saveProductsReordering(productCategory)">{{ $t("reorder_ok") }}</button>
                                <button v-if="productCategory.reorderEnabled && productCategory.savingReordering" :disabled="true" class="btn btn-outline-primary btn-sm">{{ $t("reorder_saving") }}</button>
                            </div>
                            <div v-if="!productCategory.reorderEnabled && !productCategories.reorderEnabled" class="col-auto">
                                <router-link :to="`/menus/${$route.params.menuId}/product-categories/${productCategory.id}`" class="btn btn-outline-secondary btn-sm">{{ $t("edit_category") }}</router-link>
                            </div>
                            <div v-if="!productCategory.reorderEnabled && !productCategories.reorderEnabled" class="col-auto">
                                <button class="btn btn-outline-danger btn-sm" @click="deleteProductCategory(productCategory)" :disabled="productCategories.deletingMap[productCategory.id]">{{productCategories.deletingMap[productCategory.id] ? $t("deleting") : $t("delete_category")}}</button>
                            </div>
                        </div>
                        <div class="alert alert-danger alert-dismissible" role="alert" v-if="productCategories.actionErrorMap[productCategory.id]">
                            {{productCategories.actionErrorMap[productCategory.id]}}
                            <button type="button" class="close" data-dismiss="alert" aria-label="Close" @click="productCategories.actionErrorMap[productCategory.id] = null">
                                <span aria-hidden="true">&times;</span>
                            </button>
                        </div>
                        <div v-if="!productCategories.reorderEnabled" class="products" :class="{'reorder-enabled': productCategory.reorderEnabled}">
                            <draggable v-model="productCategory.products" :group="'products-'+productCategory.id" :disabled="!productCategory.reorderEnabled" class="row">
                                <template v-for="(product, productCategoryProductIndex) in productCategory.products">
                                    <div :key="product.id" class="col-12 col-md-6 col-lg-4">
                                        <div class="result-entity">
                                            <h3>{{product.name}}</h3>
                                            <table class="table table-bordered">
                                                <tr>
                                                    <th :class="{'status-disabled': product.isDisabled}">{{ $t('product_status') }}:</th>
                                                  <td class="no-border-right" :class="{'status-disabled': product.isDisabled}">
                                                    {{product.isDisabled ? $t("status_disabled") : $t("status_enabled")}}
                                                  </td>
                                                  <td class="with-action shrink" :class="{'status-disabled': product.isDisabled}">
                                                    <button v-if="product.isDisabled" class="btn btn-sm btn-success"
                                                            @click="enableProduct(product)"
                                                            :disabled="products.disablingMap[product.id]">
                                                      {{products.disablingMap[product.id] ? $t("enabling") : $t("enable_button")}}
                                                    </button>
                                                    <button v-else class="btn btn-sm btn-outline-danger"
                                                            @click="disableProduct(product)"
                                                            :disabled="products.disablingMap[product.id]">
                                                      {{products.disablingMap[product.id] ? $t("disabling") : $t("disable_button")}}
                                                    </button>
                                                  </td>
                                                </tr>
                                            </table>
                                            <div class="messages alert-danger alert-dismissible" v-if="products.actionErrorMap[product.id]">
                                                {{products.actionErrorMap[product.id]}}
                                                <button type="button" class="close" data-dismiss="alert" aria-label="Close" @click="products.actionErrorMap[product.id] = null">
                                                    <span aria-hidden="true">&times;</span>
                                                </button>
                                            </div>
                                            <div class="actions">
                                                <router-link :to="`/menus/${$route.params.menuId}/products/${product.id}`" class="btn btn-sm btn-outline-secondary">
                                                    {{ $t('edit_button') }}
                                                </router-link>
                                                <button class="btn btn-sm btn-outline-secondary" @click="cloneProduct(product.id)"
                                                        :disabled="products.cloningMap[product.id]">
                                                    {{products.cloningMap[product.id] ? $t("cloning") : $t("clone_button")}}
                                                </button>
                                                <button class="btn btn-sm btn-outline-danger" @click="deleteProduct(product, productCategoryIndex, productCategoryProductIndex)"
                                                        :disabled="products.deletingMap[product.id]">
                                                    {{products.deletingMap[product.id] ? $t("deleting") : $t("delete_button")}}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </template>
                            </draggable>
                        </div>
                    </div>
                </template>
            </draggable>
        </div>
      </article>
    </app-framework>
</template>

<style lang="scss" scoped>
    .product-category {
        .product-category-header {
            align-items: center;
            margin-bottom: 0.5rem;

            h2 {
                font-size: 16px;
                margin: 4px 0;
            }
        }

        .products {
            .messages {
                position: relative;
                .close {
                    padding: 0.677rem 1.25rem;
                    z-index: 1;
                }
            }

            .status-disabled {
                background-color: #f8d7da;
                border-top-color: #f5c6cb;
                border-bottom-color: #f5c6cb;
            }

            .status-disabled:first-child {
                border-right-color: #f5c6cb;
            }
        }
    }

    .reorder-product-categories-enabled {
        .product-category-header {
            margin-bottom: 10px;

            h2 {
                padding: 0.75rem;
                border-radius: 5px;
                border-style: dashed;
                border-width: 2px;
                border-color: #999;
                cursor: move;
            }
        }
    }
</style>

<script>
    import draggable from 'vuedraggable'
    import AppFramework from "@/components/layout/AppFramework";
    import LoadingState from "@/components/states/LoadingState";
    import EmptyState from "@/components/states/EmptyState";
    import RequestErrorState from "@/components/states/RequestErrorState";
    import {DELETE_PRODUCT, REORDER_PRODUCTS} from "@/gql/Products";
    import {DELETE_PRODUCT_CATEGORY, REORDER_PRODUCT_CATEGORIES} from "@/gql/ProductCategories";
    import gql from "graphql-tag";
    import BreadCrumbs from "@/components/layout/BreadCrumbs";

    export default {
        name: 'productListing',
        i18nOptions: {namespaces: ['product_listing', 'common', 'breadcrumbs']},
        components: {BreadCrumbs, draggable, RequestErrorState, EmptyState, LoadingState, AppFramework},
        metaInfo() {
          return {
            title: this.$t('product_listing')
          }
        },
        data: () => ({
            form: {
                textSearch: "",
                active: true,
                productId: null
            },
            products: {
                data: {},
                loading: true,
                error: null,
                disablingMap: {},
                cloningMap: {},
                deletingMap: {},
                actionErrorMap: {}
            },
            productCategories: {
                data: {},
                loading: true,
                reorderEnabled: false,
                reorderError: null,
                savingReordering: false,
                error: null,
                deletingMap: {},
                actionErrorMap: {},
            },
        }),
        mounted() {
            this.queryProductCategoriesWithProducts();
        },
        methods: {
            async refreshData() {
                await Promise.all([this.queryProductCategoriesWithProducts(false)]);
            },
          async disableProduct(product) {
            this.$set(this.products.disablingMap, product.id, true);
            this.$set(this.products.actionErrorMap, product.id, null);
            try {
              await this.$apolloClient.mutate({
                mutation: gql`mutation ($id: ID!) {
                    disableProduct(
                        id: $id
                    ) {
                        id
                        isDisabled
                    }
                }`,
                variables: {
                  id: product.id
                }
              });
              product.isDisabled = true;
            } catch (e) {
              if (!e.graphQLErrors || !e.graphQLErrors.some(({extensions, message}) => {
                if (extensions && extensions.code === "SHOW_ERROR_TO_USER") {
                  this.$set(this.products.actionErrorMap, product.id, message);
                  return true;
                }
                return false;
              })) {
                this.$set(this.products.actionErrorMap, product.id, "Error disabling product");
              }
            }
            this.$set(this.products.disablingMap, product.id, false);
          },
          async enableProduct(product) {
            this.$set(this.products.disablingMap, product.id, true);
            this.$set(this.products.actionErrorMap, product.id, null);
            try {
              await this.$apolloClient.mutate({
                mutation: gql`mutation ($id: ID!) {
                    enableProduct(
                        id: $id
                    ) {
                        id
                        isDisabled
                    }
                }`,
                variables: {
                  id: product.id
                }
              });
              product.isDisabled = false;
            } catch (e) {
              if (!e.graphQLErrors || !e.graphQLErrors.some(({extensions, message}) => {
                if (extensions && extensions.code === "SHOW_ERROR_TO_USER") {
                  this.$set(this.products.actionErrorMap, product.id, message);
                  return true;
                }
                return false;
              })) {
                this.$set(this.products.actionErrorMap, product.id, "Error enabling product");
              }
            }
            this.$set(this.products.disablingMap, product.id, false);
          },

          async cloneProduct(productId) {
            this.$set(this.products.cloningMap, productId, true);
            this.$set(this.products.actionErrorMap, productId, null);
            try {
              const response = await this.$apolloClient.mutate({
                mutation: gql`mutation ($id: ID! $suffix: String) {
                    cloneProduct(
                        id: $id
                        suffix: $suffix
                    ) {
                        id
                    }
                }`,
                variables: {
                  id: productId,
                  suffix: this.$t("cloned_product_suffix")
                }
              });
              await this.$apolloClient.resetStore();
              await this.$router.push(`/menus/${this.$route.params.menuId}/products/${response.data.cloneProduct.id}`)
            } catch (e) {
              if (!e.graphQLErrors || !e.graphQLErrors.some(({extensions, message}) => {
                if (extensions && extensions.code === "SHOW_ERROR_TO_USER") {
                  this.$set(this.products.actionErrorMap, productId, message);
                  return true;
                }
                return false;
              })) {
                this.$set(this.products.actionErrorMap, productId, "Error to clone product");
              }
            }
            this.$set(this.products.cloningMap, productId, false);
          },
            async deleteProduct(product, productCategoryIndex, productCategoryProductIndex) {
                if (!confirm(this.$t("delete_product_confirm", {name: product.name}))) {
                    return
                }
                this.$set(this.products.deletingMap, product.id, true);
                this.$set(this.products.actionErrorMap, product.id, null);
                try {
                    await this.$apolloClient.mutate({
                        mutation: DELETE_PRODUCT,
                        variables: {
                            id: product.id
                        }
                    });

                    const productIndex = this.products.data.nodes.indexOf(product)
                    this.products.data.nodes.splice(productIndex, 1)
                    this.productCategories.data.nodes[productCategoryIndex].products.splice(productCategoryProductIndex, 1)
                    this.$apolloClient.cache.data.set(`Product:${product.id}`, undefined)

                    this.$set(this.products.actionErrorMap, product.id, null);
                } catch (e) {
                    if (!e.graphQLErrors || !e.graphQLErrors.some(({extensions, message}) => {
                        if (extensions && extensions.code === "SHOW_ERROR_TO_USER") {
                            this.$set(this.products.actionErrorMap, product.id, message);
                            return true;
                        }
                        return false;
                    })) {
                        this.$set(this.products.actionErrorMap, product.id, "Error deleting record");
                    }
                }
                this.$set(this.products.deletingMap, product.id, false);
            },
            async deleteProductCategory(productCategory) {
                if (!confirm(this.$t("delete_product_category_confirm", {name: productCategory.name}))) {
                    return
                }
                this.$set(this.productCategories.deletingMap, productCategory.id, true);
                this.$set(this.productCategories.actionErrorMap, productCategory.id, null);
                try {
                    await this.$apolloClient.mutate({
                        mutation: DELETE_PRODUCT_CATEGORY,
                        variables: {
                            id: productCategory.id
                        }
                    });

                  const productCategoryIndex = this.productCategories.data.nodes.indexOf(productCategory)
                  this.productCategories.data.nodes.splice(productCategoryIndex, 1)
                  this.$apolloClient.cache.data.set(`ProductCategory:${productCategory.id}`, undefined)

                  this.$set(this.productCategories.actionErrorMap, productCategory.id, null);
                } catch (e) {
                    if (!e.graphQLErrors || !e.graphQLErrors.some(({extensions, message}) => {
                        if (extensions && extensions.code === "SHOW_ERROR_TO_USER") {
                            this.$set(this.productCategories.actionErrorMap, productCategory.id, message);
                            return true;
                        }
                        return false;
                    })) {
                        this.$set(this.productCategories.actionErrorMap, productCategory.id, "Error deleting record");
                    }
                }
                this.$set(this.productCategories.deletingMap, productCategory.id, false);
            },
            async queryProductCategoriesWithProducts(useCache = true) {
                this.productCategories.loading = true;
                this.productCategories.deletingMap = {};
                this.productCategories.actionErrorMap = {};
                this.products.deletingMap = {};
                this.products.actionErrorMap = {};
                this.productCategories.reorderError = null;
                this.productCategories.savingReordering = false;
                this.productCategories.reorderEnabled = false;
                try {
                    const response = await this.$apolloClient.query({
                        query: gql`query ($menuId: ID!) {
                            productCategories (menuId: $menuId) {
                                id
                                name
                                order
                            }
                            products (menuId: $menuId) {
                                id
                                name
                                price
                                description
                                photoUrl
                                productCategoryId
                                choices {
                                    title
                                    type
                                    required
                                    minOptions
                                    maxOptions
                                    options {
                                        description
                                        details
                                        price
                                    }
                                }
                                order
                                isDisabled
                            }
                        }`,
                        variables: {
                            menuId: this.$route.params.menuId
                        },
                        fetchPolicy: useCache ? "cache-first" : "network-only"
                    });
                    this.$set(this.productCategories.data, 'nodes', response.data.productCategories);
                    this.$set(this.products.data, 'nodes', response.data.products);

                    const indexedProductCategory = {};
                    for (let i = 0; i < this.productCategories.data.nodes.length; i++) {
                        indexedProductCategory[this.productCategories.data.nodes[i].id] = i;
                        this.$set(this.productCategories.data.nodes[i], 'products', []);
                        this.$set(this.productCategories.data.nodes[i], 'reorderEnabled', false);
                        this.$set(this.productCategories.data.nodes[i], 'savingReordering', false);
                    }
                    for (let i = 0; i < this.products.data.nodes.length; i++) {
                        const product = this.products.data.nodes[i];
                        this.productCategories.data.nodes[indexedProductCategory[product.productCategoryId]].products.push(product);
                    }

                    this.productCategories.error = null;
                } catch (e) {
                    if (!e.graphQLErrors || !e.graphQLErrors.some(({extensions, message}) => {
                        if (extensions && extensions.code === "SHOW_ERROR_TO_USER") {
                            this.productCategories.error = message;
                            return true;
                        }
                        return false;
                    })) {
                        this.productCategories.error = "Error to load product categories";
                    }
                }
                this.productCategories.loading = false;
            },
            async saveProductsReordering(productCategory){
                productCategory.savingReordering = true;
                let reorderInputs = [];
                let changeDetect = false;
                for (let i = 0; i < productCategory.products.length; i++) {
                    reorderInputs.push({id: productCategory.products[i].id, order: i+1});
                    if (productCategory.products[i].order !== i+1) {
                        changeDetect = true;
                    }
                }
                if (changeDetect) {
                    try {
                        await this.$apolloClient.mutate({
                            mutation: REORDER_PRODUCTS,
                            variables: {
                                productCategoryId: productCategory.id,
                                inputs: reorderInputs
                            }
                        });
                        this.$set(this.productCategories.actionErrorMap, productCategory.id, null);
                        productCategory.reorderEnabled = false;
                        for (let i = 0; i < productCategory.products.length; i++) {
                            productCategory.products[i].order = i + 1;
                        }
                    } catch (e) {
                        if (!e.graphQLErrors || !e.graphQLErrors.some(({extensions, message}) => {
                            if (extensions && extensions.code === "SHOW_ERROR_TO_USER") {
                                this.$set(this.productCategories.actionErrorMap, productCategory.id, message);
                                return true;
                            }
                            return false;
                        })) {
                            this.$set(this.productCategories.actionErrorMap, productCategory.id, "Error reordering products");
                        }
                    }
                } else {
                    productCategory.reorderEnabled = false;
                }
                productCategory.savingReordering = false;
            },
            async saveProductCategoriesReordering(){
                this.productCategories.savingReordering = true;
                let reorderInputs = [];
                let changeDetect = false;
                for (let i = 0; i < this.productCategories.data.nodes.length; i++) {
                    reorderInputs.push({id: this.productCategories.data.nodes[i].id, order: i+1});
                    if (this.productCategories.data.nodes[i].order !== i+1) {
                        changeDetect = true;
                    }
                }
                if (changeDetect) {
                    try {
                        await this.$apolloClient.mutate({
                            mutation: REORDER_PRODUCT_CATEGORIES,
                            variables: {
                                inputs: reorderInputs
                            }
                        });
                        this.productCategories.reorderError = null;
                        this.productCategories.reorderEnabled = false;
                        for (let i = 0; i < this.productCategories.data.nodes.length; i++) {
                            this.productCategories.data.nodes[i].order = i + 1;
                        }
                    } catch (e) {
                        if (!e.graphQLErrors || !e.graphQLErrors.some(({extensions, message}) => {
                            if (extensions && extensions.code === "SHOW_ERROR_TO_USER") {
                                this.productCategories.reorderError = message;
                                return true;
                            }
                            return false;
                        })) {
                            this.productCategories.reorderError = "Error reordering categories";
                        }
                    }
                } else {
                    this.productCategories.reorderEnabled = false;
                }
                this.productCategories.savingReordering = false;
            },
        }
    }
</script>
